import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import authService from "@/services/authService";
import inboxService from "@/services/inboxService";

export default function InboxBell({
  className = "relative rounded-full p-2.5 text-slate-600 hover:bg-[#fff7e9] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]",
  iconClassName = "h-5 w-5",
  badgeClassName = "absolute right-1 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e0a51b] px-1 text-[10px] font-bold text-black",
}) {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let mounted = true;

    const loadUnreadCount = async () => {
      if (!authService.isAuthenticated()) {
        if (mounted) {
          setUnreadCount(0);
        }
        return;
      }

      try {
        const [notificationResult, threadsResult] = await Promise.allSettled([
          inboxService.getUnreadCount(),
          inboxService.getThreads({}),
        ]);

        const notificationCount =
          notificationResult.status === "fulfilled"
            ? Number(notificationResult.value?.count || 0)
            : 0;
        const threadCount =
          threadsResult.status === "fulfilled" && Array.isArray(threadsResult.value)
            ? threadsResult.value.reduce(
                (total, thread) => total + Number(thread.unread_count || 0),
                0
              )
            : 0;

        if (mounted) {
          setUnreadCount(notificationCount + threadCount);
        }
      } catch (error) {
        if (mounted) {
          setUnreadCount(0);
        }
      }
    };

    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={() => navigate("/inbox")}
      className={className}
      data-testid="notification-bell"
      aria-label="Open inbox"
    >
      <Bell className={iconClassName} />
      {unreadCount > 0 && (
        <span className={badgeClassName}>{unreadCount > 99 ? "99+" : unreadCount}</span>
      )}
    </button>
  );
}
